import jwt_decode from 'jwt-decode';

import type { RegisterParseResult } from '@utils/parseRegisterResponse';

/**
 * TA-11025 (pairs with backend TA-11024): social registration on
 * POST /api/v3|v4/users.json must carry a live, server-verifiable
 * `user.identity_token`. The backend rejects a missing / expired / mismatched
 * token with 422 `errors.identity_token` ("Unable to verify your Google
 * sign-in. Please try again.") — the only fix for that is a fresh sign-in
 * with the provider, so callers re-prompt instead of showing it as a dead-end
 * error.
 */

const IDENTITY_ERROR_PATTERN = /unable to verify your (apple|google)/i;

/** True when a failed registration was rejected for its social identity token. */
export const isSocialIdentityError = (result: RegisterParseResult): boolean => {
  if (result.status !== 'error') return false;
  if (result.fieldErrorsRaw.identity_token?.length) return true;
  return IDENTITY_ERROR_PATTERN.test(result.message ?? '');
};

/**
 * Google ID tokens live ~1h and Apple's ~10min, and RegisterForm lets the user
 * sit on the profile screen for as long as they like. Checking `exp` before
 * submitting lets the button re-prompt the provider while it still holds a
 * user gesture (a popup opened after an awaited request is blocked on web).
 *
 * Unreadable or exp-less tokens count as stale: re-prompting costs one
 * click, sending a token the backend will reject costs the whole submit.
 */
export const isIdentityTokenFresh = (
  token: string | null | undefined,
  { leewaySeconds = 60, now = Date.now() } = {}
): boolean => {
  if (!token) return false;
  try {
    const { exp } = jwt_decode<{ exp?: unknown }>(token);
    if (typeof exp !== 'number') return false;
    return exp * 1000 - leewaySeconds * 1000 > now;
  } catch {
    return false;
  }
};
